import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormReset, UseFormWatch } from 'react-hook-form';

type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const draftPrefix = 'draft:';

const getStorage = (): DraftStorage | null => {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const buildKey = (key: string): string => `${draftPrefix}${key}`;

export const loadDraft = <T>(key: string): T | null => {
  const storage = getStorage();
  if (!storage) return null;
  const stored = storage.getItem(buildKey(key));
  if (!stored) return null;
  try {
    return JSON.parse(stored) as T;
  } catch {
    storage.removeItem(buildKey(key));
    return null;
  }
};

export const saveDraft = <T>(key: string, data: T): void => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(buildKey(key), JSON.stringify(data));
  } catch {
    // ignore errors
  }
};

interface UseDraftFormParams<T extends FieldValues> {
  key: string;
  watch: UseFormWatch<T>;
  reset: UseFormReset<T>;
  enabled: boolean;
  debounceMs?: number;
}

/**
 * Persists form values to sessionStorage while the form is being edited.
 * Use for "create" flows only. For edit flows, set enabled=false to rely on server data.
 */
export const useDraftForm = <T extends FieldValues>({
  key,
  watch,
  reset,
  enabled,
  debounceMs = 400,
}: UseDraftFormParams<T>): void => {
  const debounceRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return;
    const storage = getStorage();
    if (!storage) return;
    const stored = storage.getItem(buildKey(key));
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as T;
      reset(parsed);
    } catch {
      storage.removeItem(buildKey(key));
    }
  }, [enabled, key, reset]);

  useEffect(() => {
    if (!enabled) return;
    const storage = getStorage();
    if (!storage) return;
    const subscription = watch((values) => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        try {
          storage.setItem(buildKey(key), JSON.stringify(values));
        } catch {
          // ignore write errors
        }
      }, debounceMs);
    });
    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [debounceMs, enabled, key, watch]);
};

export const clearDraft = (key: string): void => {
  const storage = getStorage();
  if (!storage) return;
  storage.removeItem(buildKey(key));
};
